import classNames from 'classnames';
import * as PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useLongPress from '../../hooks/useLongPress';
import { t } from '../../lang';
import HelperService from '../../services/HelperService';
import RequestService from '../../services/RequestService';
import TypesService from '../../services/TypesService';
import { cardSlice } from '../../store/slices';
import Button from '../Button';
import Icon, { Icons } from '../Icon/Icon';
import BlackGlare from './images/BlackGlare.png';
import Frost from './images/Frost.png';
import WhiteGlare from './images/WhiteGlare.png';

import './Card.scss';

const glareByColor = {
	transparent: null,
	black: WhiteGlare,
	white: BlackGlare,
	purple: WhiteGlare,
	blue: WhiteGlare,
	green: WhiteGlare,
};

const paymentSystemIcons = {
	mastercard: 'MASTERCARD',
	visa: 'VISA',
};

const coverInfo = {
	frozen: {
		header: 'Card is Frozen. Unfreeze it to use.',
		buttonIcon: 'SNOWFLAKE_SLASH_BOLD',
		buttonText: 'Unfreeze card',
	},
	closed: { header: 'The action cannot be undone', buttonIcon: 'CANCELED', buttonText: 'Close card' },
};

const closingGradiends = {
	closed: 'linear-gradient(0deg, rgba(255, 59, 48, 0) 0%, rgba(255, 59, 48, 0.5) 100%)',
	closing: 'linear-gradient(0deg, rgba(255, 59, 48, 0) 0%, #FF3B30 100%)',
};

const Card = ({
	className = null,
	Id = null,
	color = 'transparent',
	number = '',
	cardholderName = '',
	paymentSystem = null,
	status = 'active',
	onClick = null,
	onLongPress = null,
	copyNumberByLongPress = false,
	flipCardByPress = false,
	contentElement = null,
	coverButtonHandler = () => {},
	setFlipStatus = () => {},
	flipSignal = '',
}) => {
	const [cardWidth, setCardWidth] = useState(0);
	const [isShowCvv, setIsShowCvv] = useState(false);
	const cardRef = useRef(null);
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const cardDetails = useSelector(store => store.card.viewedCardDetails);

	const hideDetails = () => {
		dispatch(cardSlice.actions.setViewedCardDetails());
	};

	const flipCard = () => {
		const resolveFunc = response => {
			navigate(-1);
			if (response?.Status === 'Success') {
				dispatch(cardSlice.actions.setViewedCardDetails({ Id, ...response.Data }));
			} else {
				RequestService.BaseThenFunc(response);
			}
		};

		HelperService.confirmPin({ requestMethod: RequestService.GetCardData, resolveFunc });
	};

	const cardFrontClickHandler = () => {
		if (onClick) onClick();
		if (flipCardByPress) flipCard();
	};

	const copyNumber = () => {
		navigator.clipboard.writeText(number);

		HelperService.showNotification({
			icon: 'DOCUMENTS_ICON_PURPLE',
			title: t('Number card copied'),
			iconSize: 32,
		});
	};

	const cardFrontLongPressHandler = () => {
		if (onLongPress) onLongPress();
		if (copyNumberByLongPress) copyNumber();
	};

	const longPressEvent = useLongPress(cardFrontLongPressHandler, cardFrontClickHandler);

	const paymentSystemIconName = paymentSystemIcons[paymentSystem];
	const cardColors = TypesService.CardColors[color];

	const cardBackground = `${!!closingGradiends[status] ? closingGradiends[status] + ', ' : ''}linear-gradient(180deg, ${
		cardColors?.backgroundGradients[0]
	} 0.27%, ${cardColors?.backgroundGradients[1]} 99.73%)`;
	const cardBorder = `1px solid ${cardColors.border}`;
	const cardNumberEnd = number ? number.slice(-4) : '0000';

	useEffect(() => {
		const updateCardWidthData = () => setCardWidth(cardRef.current.offsetWidth);
		updateCardWidthData();

		window.addEventListener('resize', updateCardWidthData);

		return () => {
			window.removeEventListener('resize', updateCardWidthData);
		};
	}, []);

	useEffect(() => {
		typeof setFlipStatus === 'function' && setFlipStatus(cardDetails?.Id === Id);
	}, [cardDetails?.Id]);

	useEffect(() => {
		if (flipSignal === 'hideDetails') {
			hideDetails();
		} else if (flipSignal === 'showDetails') {
			cardFrontClickHandler();
		}
	}, [flipSignal]);

	const needUseLognPressHook = onLongPress || copyNumberByLongPress;
	const cardClickHandler = !needUseLognPressHook && onClick ? onClick : () => {};
	const cardFrontEventsHandler = needUseLognPressHook ? longPressEvent : { onClick: flipCardByPress || (() => {}) };

	return (
		<div
			className={classNames('Card', className, 'Card' + '__' + color, 'Card' + '__' + status)}
			style={{
				background: cardBackground,
				border: cardBorder,
				color: cardColors.textColor || '#fff',
				fontSize: cardWidth / 22.56,
			}}
			ref={cardRef}
			onClick={cardClickHandler}
		>
			{cardDetails?.Id !== Id ? (
				<div className='Card__front' {...cardFrontEventsHandler}>
					<div className={classNames('Card__number', { 'Card__number-empty': !number })}>
						{[0, 1, 2].map(i => (
							<div className='Card__number-group' key={i}>
								{[0, 1, 2, 3].map(j => (
									<span className='Card__hidden-figure' key={j}>
										*
									</span>
								))}
							</div>
						))}
						<div className='Card__number-group Card__number-end'>{cardNumberEnd}</div>
					</div>
					{!!number && status !== 'frozen' && <div className='Card__cardholder'>{cardholderName.slice(0, 20)}</div>}
					{paymentSystem && (
						<div className={classNames('Card__payment-system', 'Card__payment-system' + '__' + paymentSystem)}>
							<Icon type={Icons[paymentSystemIconName]} width={cardWidth / 6.12} height={cardWidth / 10.03} />
						</div>
					)}
				</div>
			) : (
				<div className='Card__back' onClick={hideDetails}>
					<div className='Card__back-header'>{t('Number card')}</div>
					<div className='Card__back-number-strip'>
						<div className='Card__back-number'>
							{[0, 4, 8, 12].map(i => (
								<span className='Card__back-number-group' key={i}>
									{cardDetails.Number.slice(i, i + 4)}
								</span>
							))}
							<Icon
								type={Icons.DOCUMENTS_FILL}
								className='Card__back-copy'
								width={cardWidth / 22.56}
								height={cardWidth / 22.56}
								onClick={e => {
									navigator.clipboard.writeText(cardDetails.Number);
									HelperService.showNotification({
										icon: 'DOCUMENTS_ICON_PURPLE',
										title: t('Number card copied'),
										iconSize: 32,
									});
									e.stopPropagation();
								}}
							/>
						</div>
					</div>
					<div className='Card__back-details'>
						<div className='Card__back-detail-box'>
							<div className='Card__back-detail-header'>{t('Expiration')}</div>
							<div className='Card__back-detail-content'>{cardDetails.Expiration}</div>
						</div>
						<div className='Card__back-detail-box'>
							<div className='Card__back-detail-header'>CVV</div>
							<div className='Card__back-detail-content'>
								<div className='Card__back-cvv-number'>{isShowCvv ? cardDetails.Cvv : '* * *'}</div>
								<Icon
									type={Icons[isShowCvv ? 'EYE_FILL' : 'EYE_SLASH_FILL']}
									className='Card__back-cvv-eye'
									width={cardWidth / 22.56}
									height={cardWidth / 22.56}
									onClick={e => {
										setIsShowCvv(value => !value);
										e.stopPropagation();
									}}
								/>
							</div>
						</div>
					</div>
				</div>
			)}
			{status !== 'frozen' && status !== 'closed' && !!glareByColor[color] && (
				<img className='Card__glare' src={glareByColor[color]} />
			)}
			{status === 'frozen' && <img className='Card__frost' src={Frost} />}
			{(status === 'frozen' || status === 'closed') && (
				<div className='Card__cover-box'>
					<div className='Card__cover-header'>{t(coverInfo[status].header)}</div>
					<Button
						className='Card__cover-button'
						iconBefore={coverInfo[status].buttonIcon}
						iconSize={cardWidth / 22.56}
						btnSize='small'
						onClick={coverButtonHandler}
					>
						{t(coverInfo[status].buttonText)}
					</Button>
				</div>
			)}
			{contentElement ? <div className='Card__content-element'>{contentElement}</div> : null}
		</div>
	);
};

Card.propTypes = {
	className: PropTypes.node,
	Id: PropTypes.number,
	color: PropTypes.oneOf(['transparent', 'black', 'white', 'purple', 'blue', 'green']),
	number: PropTypes.string,
	cardholderName: PropTypes.string,
	paymentSystem: PropTypes.oneOf(['mastercard', 'visa']),
	status: PropTypes.oneOf(['empty', 'active', 'frozen', 'closing', 'closed']),
	onClick: PropTypes.func,
	onLongPress: PropTypes.func,
	setFlipStatus: PropTypes.func,
	copyNumberByLongPress: PropTypes.bool,
	flipCardByPress: PropTypes.bool,
	flipSignal: PropTypes.oneOf(['', 'showDetails', 'hideDetails']),
};

export default Card;
